import { MoreHorizontal, Sparkles, Square } from "lucide-react";
import { useRef, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { IconTile } from "@/components/ui/icon-tile";
import { Popover, PopoverAnchor } from "@/components/ui/popover";

import { useWorkflows } from "../context/WorkflowsContext";
import { hasStartedWork } from "../status";
import { FirmDefaultsLink } from "./FirmDefaultsLink";
import {
  RunAllConfirm,
  RunAllIcon,
  useRunAll,
  type RunAllMode
} from "./RunAllButton";

/**
 * The top of Let CaseBlink Prepare: the lavender tile and the title, with one
 * primary run (Run Remaining once some work is done, else Run Everything),
 * the Case Preparation Settings gear, and a menu holding Run Everything when
 * Run Remaining leads (no menu otherwise, or while a run goes). Progress lives
 * in the stepper and group counts below. A run that confirms first opens its
 * popover off the actions, whether started from the button or the menu.
 */
export const WorkflowHeader: FC = () => {
  const w = useWorkflows();
  const runs = {
    everything: useRunAll("everything"),
    remaining: useRunAll("remaining")
  };
  const [confirming, setConfirming] = useState<RunAllMode | null>(null);
  const keepFocus = useRef(false);

  const primary: RunAllMode =
    hasStartedWork(w.views) && runs.remaining.count > 0
      ? "remaining"
      : "everything";
  const other: RunAllMode | null =
    primary === "remaining" ? "everything" : null;

  const start = (mode: RunAllMode) => {
    const run = runs[mode];
    if (run.confirm) setConfirming(mode);
    else run.go();
  };
  const main = runs[primary];

  return (
    <header
      className="flex flex-wrap items-center gap-x-3.5 gap-y-2"
      data-testid="workflow-header"
    >
      <IconTile
        size="xl"
        icon={Sparkles}
        className="bg-accent-soft text-accent-seed"
      />
      <h2
        className="min-w-0 flex-1 truncate text-xl font-medium text-fg"
        data-testid="workflow-title"
      >
        Let CaseBlink Prepare
      </h2>
      <Popover
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      >
        <PopoverAnchor asChild>
          <div
            className="ml-auto flex shrink-0 items-center gap-1.5"
            data-testid="workflow-launchpad-actions"
          >
            {w.running ? (
              <Button
                variant="ghost"
                size="md"
                onClick={w.stop}
                data-testid="workflow-stop"
              >
                <Square />
                Stop
              </Button>
            ) : (
              <Button
                variant="primary"
                size="md"
                disabled={main.disabled}
                onClick={() => start(primary)}
                data-testid={main.testId}
              >
                <RunAllIcon mode={primary} />
                {main.label}
              </Button>
            )}
            <FirmDefaultsLink
              label="Case Preparation Settings"
              shortLabel="Settings"
              variant="ghost"
              size="sm"
              gear
              testId="workflow-settings"
            />
            {other && !w.running ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="More Case Preparation Actions"
                    data-testid="workflow-more"
                  >
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-60"
                  onCloseAutoFocus={(e) => {
                    if (keepFocus.current) e.preventDefault();
                    keepFocus.current = false;
                  }}
                >
                  <DropdownMenuItem
                    disabled={runs[other].disabled}
                    onSelect={() => {
                      keepFocus.current = runs[other].confirm;
                      start(other);
                    }}
                    data-testid={`${runs[other].testId}-item`}
                  >
                    <RunAllIcon mode={other} accent />
                    {runs[other].label}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>
        </PopoverAnchor>
        {confirming ? (
          <RunAllConfirm
            mode={confirming}
            onClose={() => setConfirming(null)}
          />
        ) : null}
      </Popover>
    </header>
  );
};
